"use client";

import * as React from "react";
import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { useDeleteUser } from "./api/user.js";
import { AlertDialog, Button } from "./elements.js";
import { Member } from "../api/endpoint.js";
import { Translation } from "./i18n/translation.js";

interface DeleteUserDialogProps extends AlertDialog.RootProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: Member;
  children?: React.ReactNode;
}

export function DeleteUserDialog({
  children,
  user,
  ...props
}: DeleteUserDialogProps) {
  const deleteUser = useDeleteUser();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onSubmitForm = () => {
    deleteUser.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          props.onOpenChange(false);
        },
      },
    );
  };

  return (
    <AlertDialog.Root {...props}>
      {children && <AlertDialog.Trigger>{children}</AlertDialog.Trigger>}

      <AlertDialog.Content
        maxWidth="480px"
        onOpenAutoFocus={() => {
          requestAnimationFrame(() => {
            inputRef.current?.focus();
          });
        }}
      >
        <AlertDialog.Title>
          <Translation
            defaultMessage="Remove user"
            id="N8aD60"
            description="Dialog title for removing a user"
          />
        </AlertDialog.Title>
        <Flex direction="column" gap="3">
          <AlertDialog.Description>
            <Translation
              defaultMessage="Are you sure you want to remove {email}? This action is immediate and cannot be undone."
              id="4zDwGl"
              description="Warning message for removing a user"
              values={{ email: <Text weight="bold">{user.email}</Text> }}
            />
          </AlertDialog.Description>
        </Flex>

        {deleteUser.error ? (
          <Callout.Root color="red" mt="4" mb="-2">
            <Callout.Text>
              {getMutationErrorMessage(deleteUser.error)}
            </Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex gap="3" justify="end" mt="5" asChild>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmitForm();
            }}
          >
            <AlertDialog.Cancel>
              <Button variant="secondary" disabled={deleteUser.isPending}>
                <Translation
                  defaultMessage="Cancel"
                  id="hHNj31"
                  description="Cancel button text"
                />
              </Button>
            </AlertDialog.Cancel>

            <Button
              variant="destructive"
              type="submit"
              loading={deleteUser.isPending}
            >
              <Translation
                defaultMessage="Remove"
                id="DJXWAJ"
                description="Confirm button text to remove user"
              />
            </Button>
          </form>
        </Flex>
        {/* mirror errors in a live region */}
        <VisuallyHidden asChild>
          <section aria-live="polite">
            {getMutationErrorMessage(deleteUser.error)}
          </section>
        </VisuallyHidden>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}

function getMutationErrorMessage(error: unknown) {
  if (!error) {
    return null;
  }
  // TODO Handle server errors
  return "There was an error removing the user. Please try again.";
}

// Note: Error messages in getMutationErrorMessage are kept as plain strings
// since they are displayed dynamically
